import type { CSSProperties } from 'react';
import { TAB_DEFS } from '@ce/core';
import { kicker, t, divider } from '../styles';

/**
 * The arc field that bleeds off the top-right of every header — the Nocturne
 * signature. Two hairline rings plus a soft radial bloom, never a filled shape.
 */
export function HeaderArcs() {
  const ring = (size: number, top: number, right: number, opacity: number, delay: number): CSSProperties => ({
    position: 'absolute',
    top,
    right,
    width: size,
    height: size,
    borderRadius: '50%',
    border: `1px solid rgba(var(--color-accent-rgb), ${opacity})`,
    pointerEvents: 'none',
    // Offset so the two rings never breathe in unison, which would read as a
    // throb rather than as atmosphere.
    animation: `arcBreathe 9s ease-in-out ${delay}s infinite`,
    willChange: 'transform, opacity',
  });
  return (
    <>
      <div style={ring(380, -190, -150, 0.16, 0)} />
      <div style={ring(260, -140, -90, 0.1, 1.8)} />
      <div
        style={{
          position: 'absolute', top: -120, right: -60, width: 240, height: 240,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(var(--color-accent-rgb), 0.14), transparent 70%)',
          pointerEvents: 'none',
        }}
      />
    </>
  );
}

/** A single decorative ring, for cards that carry the motif at smaller scale. */
export function CardArc({ size, top, right, opacity = 0.18, pulse, delay = 0 }: {
  size: number; top: number; right: number; opacity?: number;
  /** Slow outward ping. Off by default — most arcs sit behind text. */
  pulse?: boolean; delay?: number;
}) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute', right, top, width: size, height: size,
        borderRadius: '50%',
        // Template-literal alpha, which is why the earlier sweep to theme
        // tokens missed this one — it only matched numeric alphas.
        border: `1px solid rgba(var(--color-accent-rgb), ${opacity})`,
        pointerEvents: 'none',
        ...(pulse
          ? {
              animation: `arcPulse 7s cubic-bezier(.2,.6,.3,1) ${delay}s infinite`,
              willChange: 'transform, opacity',
            }
          : null),
      }}
    />
  );
}

export function Header({
  kicker: k,
  title,
  cartCount,
  showGrid,
  onGrid,
  onSearch,
  onCart,
}: {
  kicker: string;
  title: string;
  cartCount: number;
  /** The list/grid toggle only appears where there is a schedule to reshape. */
  showGrid: boolean;
  onGrid: () => void;
  onSearch: () => void;
  onCart: () => void;
}) {
  return (
    <div style={{ flex: 'none', padding: '56px 20px 12px', position: 'relative', overflow: 'hidden' }}>
      <HeaderArcs />
      <div
        style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: 12, position: 'relative',
        }}
      >
        <div>
          <div style={kicker(9.5, 'var(--color-accent-300)', 0.2)}>{k}</div>
          <h3 style={{ fontSize: 25, letterSpacing: '-0.02em', margin: '7px 0 0', fontWeight: 500 }}>
            {title}
          </h3>
        </div>
        <div style={{ display: 'flex', gap: 8, flex: 'none' }}>
          {showGrid && (
          <button
            type="button"
            onClick={onGrid}
            title="Grid view"
            aria-label="Grid view"
            style={{
              width: 38, height: 38, flex: 'none', borderRadius: 10,
              border: `1px solid ${divider}`, background: 'transparent', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
              justifyContent: 'center', gap: 3, padding: '0 9px',
            }}
          >
            <div style={{ height: 2, width: '100%', borderRadius: 2, background: 'var(--color-accent)' }} />
            <div style={{ height: 2, width: '62%', borderRadius: 2, background: t(0.55) }} />
            <div style={{ height: 2, width: '84%', borderRadius: 2, background: t(0.35) }} />
          </button>
          )}
          <button
            type="button"
            onClick={onSearch}
            title="Search"
            aria-label="Search"
            style={{
              width: 38, height: 38, flex: 'none', borderRadius: 10,
              border: `1px solid ${divider}`, background: 'transparent', cursor: 'pointer',
              display: 'grid', placeItems: 'center',
            }}
          >
            {/* Ring plus handle — the magnifier drawn with the arc motif's geometry. */}
            <div style={{ position: 'relative', width: 17, height: 17 }}>
              <div
                style={{
                  position: 'absolute', top: 0, left: 0, width: 12, height: 12,
                  borderRadius: '50%', border: `1.5px solid ${t(0.75)}`,
                }}
              />
              <div
                style={{
                  position: 'absolute', bottom: 1, right: 1, width: 7, height: 1.5,
                  borderRadius: 1, background: t(0.75), transform: 'rotate(45deg)',
                  transformOrigin: 'right center',
                }}
              />
            </div>
          </button>
          <button
            type="button"
            onClick={onCart}
            aria-label={`Cart, ${cartCount} items`}
            style={{
              position: 'relative', width: 38, height: 38, flex: 'none', borderRadius: 10,
              border: `1px solid ${divider}`, background: 'transparent', cursor: 'pointer',
              display: 'grid', placeItems: 'center',
            }}
          >
            <div
              style={{
                width: 15, height: 12, border: `1.5px solid ${t(0.75)}`,
                borderRadius: '2px 2px 4px 4px',
              }}
            />
            {cartCount > 0 && (
              <div
                style={{
                  position: 'absolute', top: -5, right: -5, minWidth: 17, height: 17,
                  padding: '0 4px', borderRadius: 9, background: 'var(--color-accent)',
                  color: 'var(--color-bg)', font: '600 10px/17px var(--font-body)', textAlign: 'center',
                }}
              >
                {cartCount}
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Tab icons are CSS masks over a background colour, so the same SVG tints to
 * the active accent without a second asset — and follows a CMS accent change.
 */
export function TabBar({ active, onGo }: { active: string; onGo: (key: string) => void }) {
  return (
    <nav
      style={{
        flex: 'none', display: 'flex', gap: 2,
        // 30px of bottom padding was reserving room for an iOS home indicator
        // that only exists on a real device. On a desktop browser, and in the
        // desktop device frame, that is just dead space. Ask the platform.
        padding: '8px 12px',
        paddingBottom: 'max(10px, env(safe-area-inset-bottom, 0px))',
        background: 'linear-gradient(to top, var(--color-bg) 60%, rgba(var(--color-bg-rgb), 0.86))',
        borderTop: `1px solid ${divider}`,
      }}
    >
      {TAB_DEFS.map((tab) => {
        const on = active === tab.key;
        const color = on ? 'var(--color-accent-300)' : t(0.42);
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onGo(tab.key)}
            aria-current={on ? 'page' : undefined}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 6, padding: '6px 0 4px', border: 0, background: 'transparent',
              cursor: 'pointer', color,
            }}
          >
            <span
              style={{
                width: 24, height: 24, flex: 'none',
                backgroundColor: on ? 'var(--color-accent-300)' : t(0.45),
                WebkitMask: `url(assets/icons/${tab.icon}.svg) center/20px 20px no-repeat`,
                mask: `url(assets/icons/${tab.icon}.svg) center/20px 20px no-repeat`,
                transition: 'background-color .18s ease',
              }}
            />
            <span style={{ font: '500 9.5px/1 var(--font-body)', letterSpacing: '0.04em' }}>
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
